/// <amd-module name="Wiki/Clients/Wit/RestClient/WorkItemTracking" />
/**
 * ---------------------------------------------------------
 * Generated file, DO NOT EDIT
 * ---------------------------------------------------------
 *
 * See following wiki page for instructions on how to regenerate:
 *   https://aka.ms/azure-devops-client-generation
 *
 * Configuration file:
 *   tfs\web\extensions\wiki\vss-wiki-web\clients\wit\genclient.json
 */

import { IVssPageContext, IVssRestClient, IVssRestClientOptions, RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";

import * as VS_Services from "VSS/Platform/Contracts/WebApi";
import * as WorkItemTracking from "Wiki/Clients/Wit/Contracts/WorkItemTracking";

export interface IWorkItemTrackingRestClient extends IVssRestClient {
    /**
     * Queries work items linked to a given list of artifact URI.
     * 
     * @param artifactUriQuery - Defines a list of artifact URI for querying work items.
     * @param project - Project ID or project name
     */
    queryWorkItemsForArtifactUris(artifactUriQuery: WorkItemTracking.ArtifactUriQuery, project?: string): Promise<WorkItemTracking.ArtifactUriQueryResult>;
    /**
     * Gets the root queries and their children
     * 
     * @param project - Project ID or project name
     * @param expand - Include the query string (wiql), clauses, query result columns, and sort options in the results.
     * @param depth - In the folder of queries, return child queries and folders to this depth.
     * @param includeDeleted - Include deleted queries and folders
     */
    getQueries(project: string, expand?: WorkItemTracking.QueryExpand, depth?: number, includeDeleted?: boolean): Promise<WorkItemTracking.QueryHierarchyItem[]>;
    /**
     * Retrieves an individual query and its children
     * 
     * @param project - Project ID or project name
     * @param query - ID or path of the query.
     * @param expand - Include the query string (wiql), clauses, query result columns, and sort options in the results.
     * @param depth - In the folder of queries, return child queries and folders to this depth.
     * @param includeDeleted - Include deleted queries and folders
     * @param useIsoDateFormat - DateTime query clauses will be formatted using a ISO 8601 compliant format
     */
    getQuery(project: string, query: string, expand?: WorkItemTracking.QueryExpand, depth?: number, includeDeleted?: boolean, useIsoDateFormat?: boolean): Promise<WorkItemTracking.QueryHierarchyItem>;
    /**
     * Searches all queries the user has access to in the current project
     * 
     * @param project - Project ID or project name
     * @param filter - The text to filter the queries with.
     * @param top - The number of queries to return (Default is 50 and maximum is 200).
     * @param expand - 
     * @param includeDeleted - Include deleted queries and folders
     */
    searchQueries(project: string, filter: string, top?: number, expand?: WorkItemTracking.QueryExpand, includeDeleted?: boolean): Promise<WorkItemTracking.QueryHierarchyItemsResult>;
    /**
     * Gets a list of queries by ids (Maximum 1000)
     * 
     * @param queryGetRequest - 
     * @param project - Project ID or project name
     */
    getQueriesBatch(queryGetRequest: WorkItemTracking.QueryBatchGetRequest, project: string): Promise<WorkItemTracking.QueryHierarchyItem[]>;
    /**
     * Gets the results of the query given the query ID.
     * 
     * @param id - The query ID.
     * @param project - Project ID or project name
     * @param team - Team ID or team name
     * @param timePrecision - Whether or not to use time precision.
     * @param top - The max number of results to return.
     */
    queryById(id: string, project?: string, team?: string, timePrecision?: boolean, top?: number): Promise<WorkItemTracking.WorkItemQueryResult>;
    /**
     * Returns a single work item.
     * 
     * @param id - The work item id
     * @param project - Project ID or project name
     * @param fields - Comma-separated list of requested fields
     * @param asOf - AsOf UTC date time string
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     */
    getWorkItem(id: number, project?: string, fields?: string[], asOf?: Date, expand?: WorkItemTracking.WorkItemExpand): Promise<WorkItemTracking.WorkItem>;
    /**
     * Returns a list of work items (Maximum 200)
     * 
     * @param ids - The comma-separated list of requested work item ids. (Maximum 200 ids allowed).
     * @param project - Project ID or project name
     * @param fields - Comma-separated list of requested fields
     * @param asOf - AsOf UTC date time string
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     * @param errorPolicy - The flag to control error policy in a bulk get work items request. Possible options are \{Fail, Omit\}.
     */
    getWorkItems(ids: number[], project?: string, fields?: string[], asOf?: Date, expand?: WorkItemTracking.WorkItemExpand, errorPolicy?: WorkItemTracking.WorkItemErrorPolicy): Promise<WorkItemTracking.WorkItem[]>;
    /**
     * Updates a single work item.
     * 
     * @param document - The JSON Patch document representing the update
     * @param id - The id of the work item to update
     * @param project - Project ID or project name
     * @param validateOnly - Indicate if you only want to validate the changes without saving the work item
     * @param bypassRules - Do not enforce the work item type rules on this update
     * @param suppressNotifications - Do not fire any notifications for this change
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     */
    updateWorkItem(document: VS_Services.JsonPatchDocument, id: number, project?: string, validateOnly?: boolean, bypassRules?: boolean, suppressNotifications?: boolean, expand?: WorkItemTracking.WorkItemExpand): Promise<WorkItemTracking.WorkItem>;
}

class WorkItemTrackingRestClient extends RestClientBase implements IWorkItemTrackingRestClient {
    constructor(options: IVssRestClientOptions) {
        super(options);
    }

    /**
     * Queries work items linked to a given list of artifact URI.
     * 
     * @param artifactUriQuery - Defines a list of artifact URI for querying work items.
     * @param project - Project ID or project name
     */
    public async queryWorkItemsForArtifactUris(
        artifactUriQuery: WorkItemTracking.ArtifactUriQuery,
        project?: string
        ): Promise<WorkItemTracking.ArtifactUriQueryResult> {

        return this.beginRequest<WorkItemTracking.ArtifactUriQueryResult>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wit/artifactUriQuery",
            routeValues: {
                project: project
            },
            body: artifactUriQuery
        });
    }

    /**
     * Gets the root queries and their children
     * 
     * @param project - Project ID or project name
     * @param expand - Include the query string (wiql), clauses, query result columns, and sort options in the results.
     * @param depth - In the folder of queries, return child queries and folders to this depth.
     * @param includeDeleted - Include deleted queries and folders
     */
    public async getQueries(
        project: string,
        expand?: WorkItemTracking.QueryExpand,
        depth?: number,
        includeDeleted?: boolean
        ): Promise<WorkItemTracking.QueryHierarchyItem[]> {

        const queryValues: any = {
            '$expand': expand,
            '$depth': depth,
            '$includeDeleted': includeDeleted
        };

        return this.beginRequest<WorkItemTracking.QueryHierarchyItem[]>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/_apis/wit/queries/{*query}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Retrieves an individual query and its children
     * 
     * @param project - Project ID or project name
     * @param query - ID or path of the query.
     * @param expand - Include the query string (wiql), clauses, query result columns, and sort options in the results.
     * @param depth - In the folder of queries, return child queries and folders to this depth.
     * @param includeDeleted - Include deleted queries and folders
     * @param useIsoDateFormat - DateTime query clauses will be formatted using a ISO 8601 compliant format
     */
    public async getQuery(
        project: string,
        query: string,
        expand?: WorkItemTracking.QueryExpand,
        depth?: number,
        includeDeleted?: boolean,
        useIsoDateFormat?: boolean
        ): Promise<WorkItemTracking.QueryHierarchyItem> {

        const queryValues: any = {
            '$expand': expand,
            '$depth': depth,
            '$includeDeleted': includeDeleted,
            '$useIsoDateFormat': useIsoDateFormat
        };

        return this.beginRequest<WorkItemTracking.QueryHierarchyItem>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/_apis/wit/queries/{*query}",
            debounce: true,
            routeValues: {
                project: project,
                query: query
            },
            queryParams: queryValues
        });
    }

    /**
     * Searches all queries the user has access to in the current project
     * 
     * @param project - Project ID or project name
     * @param filter - The text to filter the queries with.
     * @param top - The number of queries to return (Default is 50 and maximum is 200).
     * @param expand - 
     * @param includeDeleted - Include deleted queries and folders
     */
    public async searchQueries(
        project: string,
        filter: string,
        top?: number,
        expand?: WorkItemTracking.QueryExpand,
        includeDeleted?: boolean
        ): Promise<WorkItemTracking.QueryHierarchyItemsResult> {

        const queryValues: any = {
            '$filter': filter,
            '$top': top,
            '$expand': expand,
            '$includeDeleted': includeDeleted
        };

        return this.beginRequest<WorkItemTracking.QueryHierarchyItemsResult>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/_apis/wit/queries/{*query}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Gets a list of queries by ids (Maximum 1000)
     * 
     * @param queryGetRequest - 
     * @param project - Project ID or project name
     */
    public async getQueriesBatch(
        queryGetRequest: WorkItemTracking.QueryBatchGetRequest,
        project: string
        ): Promise<WorkItemTracking.QueryHierarchyItem[]> {

        return this.beginRequest<WorkItemTracking.QueryHierarchyItem[]>({
            apiVersion: "5.0-preview.1",
            method: "POST",
            routeTemplate: "{project}/_apis/wit/queriesBatch",
            routeValues: {
                project: project
            },
            body: queryGetRequest
        });
    }

    /**
     * Gets the results of the query given the query ID.
     * 
     * @param id - The query ID.
     * @param project - Project ID or project name
     * @param team - Team ID or team name
     * @param timePrecision - Whether or not to use time precision.
     * @param top - The max number of results to return.
     */
    public async queryById(
        id: string,
        project?: string,
        team?: string,
        timePrecision?: boolean,
        top?: number
        ): Promise<WorkItemTracking.WorkItemQueryResult> {

        const queryValues: any = {
            timePrecision: timePrecision,
            '$top': top
        };

        return this.beginRequest<WorkItemTracking.WorkItemQueryResult>({
            apiVersion: "5.0-preview.2",
            routeTemplate: "{project}/{team}/_apis/wit/wiql/{id}",
            routeValues: {
                project: project,
                team: team,
                id: id
            },
            queryParams: queryValues
        });
    }

    /**
     * Returns a single work item.
     * 
     * @param id - The work item id
     * @param project - Project ID or project name
     * @param fields - Comma-separated list of requested fields
     * @param asOf - AsOf UTC date time string
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     */
    public async getWorkItem(
        id: number,
        project?: string,
        fields?: string[],
        asOf?: Date,
        expand?: WorkItemTracking.WorkItemExpand
        ): Promise<WorkItemTracking.WorkItem> {

        const queryValues: any = {
            fields: fields && fields.join(","),
            asOf: asOf,
            '$expand': expand
        };

        return this.beginRequest<WorkItemTracking.WorkItem>({
            apiVersion: "5.0-preview.3",
            routeTemplate: "{project}/_apis/wit/workItems/{id}",
            routeValues: {
                project: project,
                id: id
            },
            queryParams: queryValues
        });
    }

    /**
     * Returns a list of work items (Maximum 200)
     * 
     * @param ids - The comma-separated list of requested work item ids. (Maximum 200 ids allowed).
     * @param project - Project ID or project name
     * @param fields - Comma-separated list of requested fields
     * @param asOf - AsOf UTC date time string
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     * @param errorPolicy - The flag to control error policy in a bulk get work items request. Possible options are \{Fail, Omit\}.
     */
    public async getWorkItems(
        ids: number[],
        project?: string,
        fields?: string[],
        asOf?: Date,
        expand?: WorkItemTracking.WorkItemExpand,
        errorPolicy?: WorkItemTracking.WorkItemErrorPolicy
        ): Promise<WorkItemTracking.WorkItem[]> {

        const queryValues: any = {
            ids: ids && ids.join(","),
            fields: fields && fields.join(","),
            asOf: asOf,
            '$expand': expand,
            errorPolicy: errorPolicy
        };

        return this.beginRequest<WorkItemTracking.WorkItem[]>({
            apiVersion: "5.0-preview.3",
            routeTemplate: "{project}/_apis/wit/workItems/{id}",
            routeValues: {
                project: project
            },
            queryParams: queryValues
        });
    }

    /**
     * Updates a single work item.
     * 
     * @param document - The JSON Patch document representing the update
     * @param id - The id of the work item to update
     * @param project - Project ID or project name
     * @param validateOnly - Indicate if you only want to validate the changes without saving the work item
     * @param bypassRules - Do not enforce the work item type rules on this update
     * @param suppressNotifications - Do not fire any notifications for this change
     * @param expand - The expand parameters for work item attributes. Possible options are \{ None, Relations, Fields, Links, All \}.
     */
    public async updateWorkItem(
        document: VS_Services.JsonPatchDocument,
        id: number,
        project?: string,
        validateOnly?: boolean,
        bypassRules?: boolean,
        suppressNotifications?: boolean,
        expand?: WorkItemTracking.WorkItemExpand
        ): Promise<WorkItemTracking.WorkItem> {

        const queryValues: any = {
            validateOnly: validateOnly,
            bypassRules: bypassRules,
            suppressNotifications: suppressNotifications,
            '$expand': expand
        };

        return this.beginRequest<WorkItemTracking.WorkItem>({
            apiVersion: "5.0-preview.3",
            method: "PATCH",
            routeTemplate: "{project}/_apis/wit/workItems/{id}",
            routeValues: {
                project: project,
                id: id
            },
            customHeaders: {
                "Content-Type": "application/json-patch+json",
            },
            queryParams: queryValues,
            body: document
        });
    }

}

export const WorkItemTrackingClientName = "Wiki_Wit_5.0.IWorkItemTrackingRestClient";

export function getWorkItemTrackingClient(pageContext: IVssPageContext, options?: IVssRestClientOptions): IWorkItemTrackingRestClient {
    return pageContext.getRestClient<IWorkItemTrackingRestClient>(WorkItemTrackingClientName, options);
}

RestClients.add(WorkItemTrackingClientName, { 
    factory: WorkItemTrackingRestClient,
    options: {
        resourceAreaId: "5264459e-e5e0-4bd8-b118-0985e68a4ec5",
        serviceInstanceType: "00025394-6065-48ca-87d9-7f5672854ef7"
    }
});
